import { z } from 'zod';
import { PersonRef } from './people.js';
/**
 * Catch-ups (docs/adr/0057-catch-ups.md): live audio and screen share in a channel, peer to peer (WebRTC mesh).
 * The server keeps who is in which catch-up and relays signalling between them over the realtime socket; the
 * audio and screens never pass through it (except through a TURN relay, still encrypted end to end).
 *
 * Wording: the noun is "catch-up" (with a hyphen), the verb and the button are "Catch up". Never "catchup".
 */
/** A mesh grows with the square of its size; six people keep it light on a laptop and a home connection. */
export const CATCH_UP_MAX_PEOPLE = 6;
/** Each person's app tells the server it is still there this often. */
export const CATCH_UP_HEARTBEAT_MS = 20_000;
/** Someone not heard from for this long is taken out of the catch-up by the sweeper. */
export const CATCH_UP_STALE_MS = 75_000;
/** TURN credentials last this long (coturn "use-auth-secret": the expiry is inside the username). */
export const TURN_CREDENTIAL_TTL_SECONDS = 600;
export const CATCH_UP_FULL_MESSAGE = `This catch-up is full (${CATCH_UP_MAX_PEOPLE} people). Try again when someone leaves.`;
export const CATCH_UP_CLIENTS_OFF_MESSAGE = "Clients can't join catch-ups in this workspace.";
/**
 * One open copy of the app in the catch-up: a random id the app makes when it joins. Signals are addressed to
 * it, so a second tab or the pop-out can take over the call without the others confusing the two.
 */
export const ConnId = z.string().regex(/^[A-Za-z0-9_-]{16,64}$/);
export const CatchUpParticipant = z.object({
    person: PersonRef,
    connId: ConnId,
    muted: z.boolean(),
    sharing: z.boolean(),
    joinedAt: z.string(),
});
export const CatchUp = z.object({
    id: z.uuid(),
    channelId: z.uuid(),
    startedBy: PersonRef.nullable(),
    startedAt: z.string(),
    participants: z.array(CatchUpParticipant),
});
export const CatchUpList = z.object({ items: z.array(CatchUp) });
export const JoinCatchUpInput = z.object({ connId: ConnId });
export const LeaveCatchUpInput = z.object({ connId: ConnId });
/** Sent when muting, sharing or stopping, and every CATCH_UP_HEARTBEAT_MS as the heartbeat. */
export const CatchUpStateInput = z.object({ connId: ConnId, muted: z.boolean(), sharing: z.boolean() });
export const IceServer = z.object({
    urls: z.array(z.string().regex(/^(stun|stuns|turn|turns):[^\s]{3,200}$/)).min(1).max(8),
    username: z.string().max(200).optional(),
    credential: z.string().max(200).optional(),
});
/** Empty when the server has none: calls then work between people on the same network only. */
export const IceConfig = z.object({
    iceServers: z.array(IceServer),
    /** When the TURN credentials stop working; null without TURN. Fetch again before joining after this. */
    expiresAt: z.string().nullable(),
});
// --- Signalling over the realtime socket --------------------------------------------------------------
const Sdp = z.string().min(1).max(20_000);
export const CatchUpSignal = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('offer'), sdp: Sdp }),
    z.object({ kind: z.literal('answer'), sdp: Sdp }),
    z.object({
        kind: z.literal('ice'),
        candidate: z.object({
            candidate: z.string().max(2_000),
            sdpMid: z.string().max(64).nullable(),
            sdpMLineIndex: z.number().int().min(0).max(64).nullable(),
        }),
    }),
]);
/** What an app sends on the socket. The gateway relays it only between two people in the same catch-up. */
export const CatchUpSignalFrame = z.object({
    type: z.literal('catch_up.signal'),
    catchUpId: z.uuid(),
    fromConnId: ConnId,
    toConnId: ConnId,
    signal: CatchUpSignal,
});
/** At most this many signal frames per socket per window; the rest are dropped. */
export const SIGNAL_RATE = { limit: 200, windowMs: 10_000 };
// --- Shared wording (web and phone) ---------------------------------------------------------------------
/** 0:42, 18:05, 1:02:09. */
export function formatElapsed(ms) {
    const total = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const ss = String(s).padStart(2, '0');
    return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}
/** "Mia, Sam and 2 others": first names, at most two, then a count. */
export function catchUpNames(people, selfName) {
    const firsts = people.map((p) => (p.name === selfName ? 'You' : p.name.split(' ')[0] || p.name));
    if (firsts.length <= 2)
        return firsts.join(' and ');
    if (firsts.length === 3)
        return `${firsts[0]}, ${firsts[1]} and ${firsts[2]}`;
    return `${firsts[0]}, ${firsts[1]} and ${firsts.length - 2} others`;
}
/** "Catch-up in progress · Mia and Sam · 4:12" */
export function catchUpBanner(c, now) {
    return `Catch-up in progress · ${catchUpNames(c.participants.map((p) => p.person))} · ${formatElapsed(now - Date.parse(c.startedAt))}`;
}
/** "Catch-up ended · 18 min · 3 people" */
export function catchUpEndedText(minutes, people) {
    const mins = minutes < 1 ? 'under a minute' : `${minutes} min`;
    return `Catch-up ended · ${mins} · ${people} ${people === 1 ? 'person' : 'people'}`;
}
export const IN_A_CATCH_UP = 'In a catch-up';
/** Whether this person may join catch-ups at all (the server checks the channel too): the team, and clients when allowed. */
export function mayJoinCatchUps(role, clientsInCatchUps) {
    return role !== 'guest' || clientsInCatchUps;
}
//# sourceMappingURL=catch-ups.js.map